// One deal's own page (module 10 U151; the brief DEALS-PAGE-BRIEF-2026-09-22.md section 6; the grid
// section of screens/2026-09-22-deals-concepts/concept-b-supply.html).
//
// The deal's card at the top, and under it "Selling it": one row per operator the rights have been
// offered to, with filters Everyone, Yours and Heard.
//
// U152 again: every cell below is a column of crm.offer. Where the sheet said nothing the cell is
// an em dash and the state reads "the sheet does not say" -- never "not offered", which is a
// different claim about the same row (track `dealsdata`, D265).
import { ActionButton, CompanyLogo as Logo } from '../parts/index.js';
import { useMemo, useState } from 'react';
import { formatDate } from '../labels.js';
import { Loading, useLoad, type PageProps } from '../records/shared.js';
import { dealsApi, DealsError, type DealHead, type Offer, type RightsTerms } from './api.js';
import { DealHistoryPage } from './DealHistory.js';
import { putAsideWords } from './live-words.js';
import './deals.css';
import { DealCloseState } from './ClosedDeals.js';

type Which = 'everyone' | 'yours' | 'show' | 'heard';

/**
 * An offer's state in the reader's words. `not_stated` and `not_offered` are DIFFERENT CLAIMS and
 * are never collapsed: the first says the sheet was silent, the second that nobody has taken it to
 * them. The seed makes 49 of the 57 Padel rows `not_stated`, so getting this wrong would tell the
 * team that nobody had offered Padel to most of the market.
 */
export function stateWords(state: string): string {
  switch (state) {
    case 'not_stated': return 'The sheet does not say';
    case 'not_offered': return 'Not offered yet';
    case 'offered': return 'Offered';
    case 'talking': return 'Talking';
    case 'terms': return 'Terms';
    // The same steps as a deal's, named as the store names them since migration 195 (U237).
    case 'paper_out': return 'Proposal sent';
    case 'to_sign': return 'Contracting';
    case 'signed': return 'Signed';
    case 'said_no': return 'Said no';
    default: return state;
  }
}

/** The fee as it was written, in its own currency; nothing is converted on a row (D117). */
export function feeWords(o: Offer): string {
  // The sheet's own words win when they say something the amount does not ("£600,000", "per match"). A bare number
  // that IS the amount is drawn as the amount, so its currency is not lost: the sheet's "30" is 30 USD.
  const bare = o.feeText !== null && /^[\d,.\s]+$/.test(o.feeText) && o.feeAmount !== null
    && Number(o.feeText.replace(/[,\s]/g, '')) === Number(o.feeAmount);
  if (o.feeText && !bare) return o.feeText;
  if (!o.feeAmount) return '—';
  const n = Number(o.feeAmount);
  const shown = Number.isFinite(n) ? n.toLocaleString('en-GB', { maximumFractionDigits: n % 1 === 0 ? 0 : 2 }) : o.feeAmount;
  return o.feeCurrency ? `${shown} ${o.feeCurrency}` : shown;
}

/**
 * The state or the step the deal is at, in the same words the desk uses. U153 keeps them apart:
 * parked and not of interest are states, and a parked deal reads "Parked until 3 Oct" rather than
 * the step it happened to be sitting at.
 */
export function headWords(d: DealHead): string {
  if (d.lifecycle === 'won') return 'Won';
  if (d.lifecycle === 'lost') return 'Lost';
  // BUGS.md X8: a deal off Deals, put aside or archived, says so before anything else, as the company record names it.
  const aside = putAsideWords(d.putAside);
  if (aside) return aside;
  if (d.lifecycle === 'paused') return d.pausedUntil ? `Parked until ${formatDate(d.pausedUntil)}` : 'Parked';
  if (d.lifecycle === 'declined') return 'Not of interest';
  return d.stageName;
}

/**
 * A rights deal's terms (BUGS.md D82), each in Add deal's own labels and words: "Which rights" (Video, Data), "Exclusive"
 * (Yes, No, Not known), "Territory" and "Term" ("1 Jan 2027 to 31 Dec 2029"). A term nothing was said of is left out;
 * none at all, and there is nothing to draw.
 */
export function rightsTermsRows(t: RightsTerms | null | undefined): Array<[string, string]> {
  if (!t) return [];
  const rows: Array<[string, string]> = [];
  const which = [t.video ? 'Video' : null, t.data ? 'Data' : null].filter(Boolean).join(', ');
  if (which) rows.push(['Which rights', which]);
  const excl = t.exclusive === 'yes' ? 'Yes' : t.exclusive === 'no' ? 'No' : t.exclusive === 'unknown' ? 'Not known' : null;
  if (excl) rows.push(['Exclusive', excl]);
  if (t.territory?.trim()) rows.push(['Territory', t.territory.trim()]);
  if (t.from && t.until) rows.push(['Term', `${formatDate(t.from)} to ${formatDate(t.until)}`]);
  else if (t.from) rows.push(['Term', `From ${formatDate(t.from)}`]);
  else if (t.until) rows.push(['Term', `Until ${formatDate(t.until)}`]);
  return rows;
}

/** An operator's meeting at a show, as a fact: the show and the day (the brief's next fixed time on a roll-out row). */
export function showWords(s: { event: string; at: string | null; on: string | null }): string {
  const day = s.at ?? s.on;
  return day ? `Meeting at ${s.event}, ${formatDate(day)}` : `Meeting at ${s.event}`;
}

/**
 * Where a deal's messages and changes are (BUGS.md D88): /deals/:id for an opportunity we sell, which is its history
 * page; /deals/:id?view=history for a rights deal, whose /deals/:id is its roll-out, "Selling it". The card's foot
 * (LiveCard.tsx) writes the same path inline.
 */
export function dealHistoryPath(id: string, side: string | null): string {
  return `/deals/${encodeURIComponent(id)}${side === 'rights' ? '?view=history' : ''}`;
}

/** Whether the address asks for the history page rather than the roll-out (`?view=history`). */
function historyAsked(): boolean {
  try { return new URLSearchParams(window.location.search).get('view') === 'history'; } catch { return false; }
}

export function DealPage({ store, open, id }: PageProps & { id: string }) {
  // BUGS.md D147: a deal Add deal's Undo took back, or that was removed, answers 404, which no Try again can mend; it is
  // said as gone (null), not as a read that failed.
  const [state, reload] = useLoad(() => dealsApi.offers(store.tenantId, id).catch((e: unknown) => {
    if (e instanceof DealsError && e.status === 404) return null;
    throw e;
  }), [store.tenantId, id]);
  const [which, setWhich] = useState<Which>('everyone');

  const shown = useMemo(() => {
    if (state.kind !== 'ready' || state.data === null) return [];
    const all = state.data.offers;
    // "Yours" means the reader holds the operator, which the read now answers (crm.offer's own
    // holder against app.current_principal()). It used to mean "somebody holds it", and the chip
    // said so, which was a departure from the brief nobody had asked about.
    if (which === 'yours') return all.filter((o) => o.mine);
    if (which === 'heard') return all.filter((o) => o.source === 'signal' || o.lastHeardAt !== null);
    if (which === 'show') return all.filter((o) => o.atShow !== null);
    return all;
  }, [state, which]);

  if (state.kind === 'loading') return <div className="deals"><Loading what="the operators" /></div>;
  if (state.kind === 'error') {
    return (
      <div className="deals">
        <div className="dl-says">
          <div className="dl-err">The operators did not load. Nothing was changed.</div>
          <div className="dl-opts"><ActionButton reading type="button" className="dl-opt" onClick={reload}>Try again</ActionButton></div>
        </div>
      </div>
    );
  }

  if (state.data === null) {
    return (
      <div className="deals">
        <div className="dl-says">
          <div className="dl-err">That deal is not on Deals: it was taken back or removed. Nothing was changed.</div>
          <div className="dl-opts"><ActionButton reading type="button" className="dl-opt" onClick={() => open('/deals')}>Back to Deals</ActionButton></div>
        </div>
      </div>
    );
  }

  const deal = state.data.deal;
  // An opportunity we sell is the history page (pipeline P5 step 7, U239); a rights deal keeps its roll-out (U151), and
  // its messages and changes are the same history page at ?view=history (BUGS.md D88), which its card's "Everything on
  // this deal" and the roll-out's own link open.
  if (deal.side !== 'rights' || historyAsked()) return <DealHistoryPage store={store} open={open} id={id} head={deal} />;
  const offers = state.data.offers;
  const terms = rightsTermsRows(deal.rightsTerms);
  const signed = offers.filter((o) => o.state === 'signed').length;
  const offered = offers.filter((o) => o.state !== 'not_stated' && o.state !== 'not_offered').length;

  return (
    <div className="deals">
      <div className="dl-top">
        <h1 className="dl-title">{deal.title}</h1>
        <ActionButton reading type="button" className="dl-opt" onClick={() => open(dealHistoryPath(deal.id, deal.side))}>Everything on this deal</ActionButton>
        <ActionButton reading type="button" className="dl-opt" onClick={() => open('/deals')}>Back to Deals</ActionButton>
      </div>

      {/* WHICH DEAL THIS IS, before anything is said about it. The page was headed "Selling it"
          with no title, no company and no state, so a reader arriving on it from a link had no
          way of telling one deal's roll-out from another's. */}
      <article className="dl-card">
        <div className="dl-meta">
          {deal.side === 'rights' ? <span className="dl-side">Rights</span> : deal.side === 'sales' ? <span className="dl-side">Sales</span> : null}
          {/* The logo of the company the deal is filed on (pipeline RC8; U359, U371), beside its name. */}
          {/* BUGS.md D97: a company put away is named nowhere and has no record to open. */}
          {deal.organisationName !== null
            ? <span className="co-with-logo"><Logo name={deal.organisationName} logo={deal.organisationLogo ?? null} size="sm" /><ActionButton reading type="button" className="dotted" onClick={() => open(`/companies/${deal.organisationId}`)}>{deal.organisationName}</ActionButton></span>
            : null}
          <span>{deal.mine ? 'You hold it' : deal.holderName ? `${deal.holderName} holds it` : 'Nobody holds it'}</span>
          <span className="dl-state">{headWords(deal)}</span>
          {deal.lifecycle === 'won' || deal.lifecycle === 'lost' ? <DealCloseState store={store.tenantId} id={id} open={open} changed={reload} stamp={deal} /> : null}
          {/* BUGS.md D73 (Mike, by pop-up, 8 Oct about 12:37 CEST: "show them; the contact in the card's facts and the deal
              page's head"): their contact, opening their page. */}
          {deal.contact ? <span>Their contact: <ActionButton reading type="button" className="dotted" onClick={() => open(`/people/${encodeURIComponent(deal.contact!.id)}`)}>{deal.contact.name}</ActionButton></span> : null}
          {/* BUGS.md D129 (Mike by pop-up, 8 Oct 2026, "All as recommended (Recommended)": "a rights deal's page names its
              property"): the right it is on, opening the right's own page. */}
          {deal.property ? <span>The right: <ActionButton reading type="button" className="dotted" onClick={() => open(`/rights/${encodeURIComponent(deal.property!.id)}`)}>{deal.property.name}</ActionButton></span> : null}
        </div>
        {/* BUGS.md D82 (Mike, by pop-up, 8 Oct about 12:37 CEST: "the terms on the rights deal's page"): the terms Add deal
            saved, under the title, in its own labels. */}
        {terms.length > 0
          ? <dl className="dl-terms" aria-label="The terms">{terms.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
          : null}
        {deal.stateReason ? <div className="dl-quote">“{deal.stateReason}”</div> : null}
      </article>

      <div className="dl-band"><h2>Selling it</h2></div>

      {offers.length === 0
        ? <div className="dl-says"><p className="gist">Nobody has been offered this yet. When someone on the team puts it to an operator, Cobrain adds the row.</p></div>
        : (
          <>
            <div className="dl-rollout">
              <div className="rh">
                <b>{offers.length} operators</b>
                <span className="c">{offered} offered, {signed} signed</span>
                <div className="dl-filters">
                  <ActionButton reading type="button" className={which === 'everyone' ? 'chip on' : 'chip'} onClick={() => setWhich('everyone')}>Everyone</ActionButton>
                  <ActionButton reading type="button" className={which === 'yours' ? 'chip on' : 'chip'} onClick={() => setWhich('yours')}>Yours</ActionButton>
                  {/* "At the show" is drawn only while an operator here has a meeting at a show still to come:
                      a chip that can only answer "none" is a filter over nothing (U152). */}
                  {offers.some((o) => o.atShow)
                    ? <ActionButton reading type="button" className={which === 'show' ? 'chip on' : 'chip'} onClick={() => setWhich('show')}>At the show</ActionButton>
                    : null}
                  <ActionButton reading type="button" className={which === 'heard' ? 'chip on' : 'chip'} onClick={() => setWhich('heard')}>Heard</ActionButton>
                </div>
              </div>
            </div>

            <div className="dl-rows" role="table">
              <div className="dl-row h" role="row">
                <span role="columnheader">Operator</span><span role="columnheader">Where it is</span>
                <span role="columnheader">Held by</span><span role="columnheader">Proposed fee</span>
                <span role="columnheader">Last heard</span>
              </div>
              {shown.length === 0
                ? <div className="dl-more">No operator under that. Choose Everyone.</div>
                : shown.map((o) => (
                  <div className="dl-row" role="row" key={o.id}>
                    {/* The label is the row (ten Entain brands are ten operators); the company is
                        the link, and 14 of the seeded rows have none. */}
                    <span role="cell">{o.organisationId
                      ? <a href={`/companies/${o.organisationId}`} onClick={(e) => { e.preventDefault(); open(`/companies/${o.organisationId}`); }}>{o.operatorLabel}</a>
                      : <b>{o.operatorLabel}</b>}
                      {o.integrationRoute ? <><br /><span className="sub">{o.integrationRoute}</span></> : null}
                      {o.atShow ? <><br /><span className="dl-atshow">{showWords(o.atShow)}</span></> : null}
                    </span>
                    <span className="c2" role="cell">{stateWords(o.state)}
                      {o.stateJudged ? <><br /><span className="sub">read by Cobrain</span></> : null}
                    </span>
                    <span className="c3" role="cell">{o.holderName ?? '—'}</span>
                    <span className="c4" role="cell">{feeWords(o)}</span>
                    <span role="cell">{o.lastHeardAt
                      ? formatDate(o.lastHeardAt)
                      : o.contactedOn
                        ? <span className="sub">Contacted {formatDate(o.contactedOn)}</span>
                        : '—'}</span>
                  </div>
                ))}
            </div>
          </>
        )}
    </div>
  );
}
